import { EXCLUSION_PREFIX, NO_DATA } from './misc.constants';
import { COLUMNS } from './columns.constants';
import type { Filters, Website, WebsitesData } from '../types';

export function search(
  where: string | number | string[],
  what: string,
): boolean {
  where = String(where).toLowerCase();
  what = String(what).toLowerCase();

  if (what.startsWith('==')) {
    return where === what.slice(2);
  } else if (what.startsWith('!=')) {
    return where !== what.slice(2);
  } else {
    return where.includes(what);
  }
}

export function filterTableData(
  websites: Website[],
  filters: Filters,
  tags: string[],
): Website[] {
  return websites.filter((website) => {
    for (const filter in filters) {
      // skip filters that are in the process of typing
      if (['', '=', '==', '!', '!='].includes(filters[filter])) {
        continue;
      }
      switch (filter) {
        case COLUMNS.pages: {
          const pages = website[filter] as string[];
          if (
            // strict equal
            (filters[filter].startsWith('==') &&
              !pages.some((page) => search(page, filters[filter]))) ||
            // strict not equal
            (filters[filter].startsWith('!=') &&
              !pages.every((page) => search(page, filters[filter]))) ||
            // includes
            (!filters[filter].startsWith('==') &&
              !filters[filter].startsWith('!=') &&
              !pages.some((page) => search(page, filters[filter])))
          ) {
            return false;
          }
          break;
        }

        default: {
          if (!website[filter] || !search(website[filter], filters[filter])) {
            return false;
          }
          break;
        }
      }
    }

    if (!tags || tags.length === 0) return true;

    // filter by tags
    const normalizedWebsiteTags = (website[COLUMNS.tags] as string[]).map(
      (tag) => tag.toLowerCase(),
    );

    const requiredTags = tags
      .filter((tag) => !tag.startsWith(EXCLUSION_PREFIX))
      .map((tag) => tag.toLowerCase());

    const excludedTags = tags
      .filter((tag) => tag.startsWith(EXCLUSION_PREFIX))
      .map((tag) => tag.substring(1).toLowerCase());

    const hasAllRequiredTags = requiredTags.every((tag) =>
      normalizedWebsiteTags.includes(tag),
    );

    const hasNoExcludedTags = excludedTags.every(
      (tag) => !normalizedWebsiteTags.includes(tag),
    );

    return hasAllRequiredTags && hasNoExcludedTags;
  });
}

export function sortTableData(array: Website[], column: string): Website[] {
  const noDataItems: Website[] = [];
  const sortedArray = [...array]
    .filter((item) => {
      if (item[column] === NO_DATA) {
        noDataItems.push(item);
        return false;
      } else {
        return true;
      }
    })
    .sort((a, b) => {
      switch (column) {
        case COLUMNS.campaignId:
          return Number(a[column]) > Number(b[column]) ? 1 : -1;

        default:
          return String(a[column]).toLowerCase() >
            String(b[column]).toLowerCase()
            ? 1
            : -1;
      }
    });

  return [...sortedArray, ...noDataItems];
}

export function getQueryParamValue(targetKey: string): string | undefined {
  const params = new URLSearchParams(window.location.search);
  for (const [key, value] of params) {
    if (key.toLowerCase() === targetKey.toLowerCase()) {
      return value;
    }
  }
}

export function setQueryParam(key: string, value: string): void {
  const searchParams = new URLSearchParams(window.location.search);
  searchParams.set(key, value);
  window.history.replaceState(
    {},
    '',
    `${window.location.pathname}?${searchParams}`,
  );
}

export function deleteQueryParam(key: string): void {
  const searchParams = new URLSearchParams(window.location.search);
  searchParams.delete(key);
  const newUrl = searchParams.toString()
    ? `${window.location.pathname}?${searchParams}`
    : window.location.pathname;
  window.history.replaceState({}, '', newUrl);
}

export function fromCamelCaseToWords(str: string): string {
  return str
    .split(/(?=[A-Z0-9])/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
    .replace(/([A-Z]+) /g, '$1');
}

export function convertUrlToEnv(website = '', env = ''): string | null {
  const lowerWebsite = website.replaceAll('.', '_').toLowerCase();

  switch (env) {
    case 'dev':
      return `${lowerWebsite}.example.com`;

    case 'prod':
      return website.toLowerCase();
  }

  return null;
}

export function triggerGtmEvent(
  eventName: string,
  options: Record<string, unknown> = {},
): void {
  if (import.meta.env.DEV) return;
  window.dataLayer?.push({
    event: eventName,
    ...options,
  });
}

export function getUniqueTags(websites: Website[]): string[] {
  const uniqueTags = websites.reduce((acc, website) => {
    website.tags.forEach((tag) => acc.add(tag));
    return acc;
  }, new Set<string>());
  return [...uniqueTags];
}

export function getUniqueValues(arr: unknown, propertyName: string): string[] {
  if (!Array.isArray(arr) || arr.length === 0) {
    return [];
  }

  if (typeof propertyName !== 'string' || propertyName === '') {
    console.error('Property name must be a non-empty string.');
    return [];
  }

  const uniqueValues = new Set<string>();

  for (const obj of arr as unknown[]) {
    if (obj && typeof obj === 'object' && Object.hasOwn(obj, propertyName)) {
      const propertyValue = (obj as Record<string, unknown>)[propertyName];
      if (Array.isArray(propertyValue)) {
        for (const value of propertyValue as unknown[]) {
          uniqueValues.add(value as string);
        }
      } else {
        uniqueValues.add(propertyValue as string);
      }
    }
  }

  return Array.from(uniqueValues);
}

export function findObjectKeyCaseInsensitive(
  key: string,
  obj: Record<string, unknown>,
): string | undefined {
  for (const objKey in obj) {
    if (
      Object.hasOwn(obj, objKey) &&
      objKey.toLowerCase() === key.toLowerCase()
    ) {
      return objKey;
    }
  }
}

export function findArrayElementCaseInsensitive(
  key: string,
  arr: string[],
): string | undefined {
  return arr.find((el) => el.toLowerCase() === key.toLowerCase());
}

export async function handleClipboardCopy(data: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(data);
  } catch (e) {
    console.log(`Failed copy data to clipboard`, (e as Error).message);
  }
}

export function filterAndSortColumns(
  columns: string[],
  state: { renderableColumns: string[]; websitesData: WebsitesData | null },
): string[] {
  const cols = state.websitesData?.columns ?? [];
  return columns
    .filter((column) => state.renderableColumns.includes(column))
    .sort(
      (a, b) =>
        cols.findIndex((col) => col.name === a) -
        cols.findIndex((col) => col.name === b),
    );
}
